import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { KeyRound, Plus, Trash2, Copy, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, formatPartnerError, canPartnerAdmin } from "./partnerApi";
import "./PartnerPortal.css";

export default function PartnerApiKeys() {
  const { me } = useOutletContext();
  const canWrite = canPartnerAdmin(me);
  const allowedScopes = me.partner_org.allowed_scopes || [];
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshToken, setFreshToken] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", scopes: [], rate_limit_per_min: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/keys");
      setKeys(data.keys || []);
    } catch (e) {
      toast.error(formatPartnerError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleScope = (s) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(s) ? prev.scopes.filter((x) => x !== s) : [...prev.scopes, s],
    }));
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/portal/keys", {
        name: form.name.trim(),
        scopes: form.scopes.length > 0 ? form.scopes : null,
        rate_limit_per_min: form.rate_limit_per_min || null,
      });
      setFreshToken(data);
      setForm({ name: "", scopes: [], rate_limit_per_min: null });
      setShowForm(false);
      load();
      toast.success("API key created — save the secret NOW.");
    } catch (e) {
      toast.error(formatPartnerError(e));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Any client using it will start receiving 401s immediately.")) return;
    try {
      await partnerApi.delete(`/partner/portal/keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  return (
    <div data-testid="pp-api-keys">
      <h1 className="hw-pp__page-title">
        <KeyRound className="w-5 h-5"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        API Keys
      </h1>
      <p className="hw-pp__page-sub">
        Mint per-integration keys scoped to a subset of your organization&apos;s allowed scopes.
        Secrets are shown ONCE at creation — save them to your secret manager immediately.
        {!canWrite && " Minting + revoking requires OWNER or ADMIN."}
      </p>

      {freshToken && (
        <div className="hw-pp__panel" data-testid="pp-fresh-key" style={{ borderColor: "#F59E0B" }}>
          <h3>
            <span>
              <AlertTriangle className="w-4 h-4"
                              style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem",
                                        color: "#F59E0B" }} />
              New API key — copy the token now
            </span>
            <button className="hw-pp__btn" onClick={() => setFreshToken(null)}>Dismiss</button>
          </h3>
          <div className="hw-pp__reveal-hint">
            HarborWell will NEVER show this token again. If you lose it, revoke and create a new key.
          </div>
          <div className="hw-pp__reveal" data-testid="pp-fresh-key-token">{freshToken.token}</div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <button className="hw-pp__btn" onClick={() => copy(freshToken.token)}
                     data-testid="pp-fresh-key-copy">
              <Copy className="w-3.5 h-3.5" /> Copy secret
            </button>
            <div style={{ fontSize: "0.75rem", color: "#94A3B8", padding: "0.4rem 0" }}>
              <strong>client_id (for OAuth):</strong> {freshToken.id}
            </div>
          </div>
          <div style={{ marginTop: "0.35rem" }}>
            {(freshToken.scopes || []).map((s) => (
              <span key={s} className="hw-pp__scope-chip">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="hw-pp__panel">
        <h3>
          <span>{keys.length} key{keys.length === 1 ? "" : "s"}</span>
          <div style={{ display: "flex", gap: "0.35rem" }}>
            <button className="hw-pp__btn" onClick={load} data-testid="pp-keys-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            {canWrite && (
              <button className="hw-pp__btn hw-pp__btn--primary"
                       onClick={() => setShowForm(!showForm)}
                       data-testid="pp-key-new">
                <Plus className="w-3.5 h-3.5" /> New key
              </button>
            )}
          </div>
        </h3>

        {showForm && (
          <div style={{ padding: "0.7rem", background: "rgba(15,23,42,0.5)",
                          borderRadius: "0.5rem", marginBottom: "0.7rem" }}>
            <label className="hw-pp__field">
              <span className="hw-pp__field-label">Key name (what this key is for)</span>
              <input className="hw-pp__input" value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      placeholder="e.g., production-referrals-service"
                      data-testid="pp-key-name" />
            </label>
            <label className="hw-pp__field">
              <span className="hw-pp__field-label">Scopes ({form.scopes.length} selected — leave empty to grant all allowed)</span>
              <div>
                {allowedScopes.map((s) => (
                  <label key={s} style={{ marginRight: "0.5rem", cursor: "pointer",
                                             fontSize: "0.8rem", color: "#CBD5E1" }}>
                    <input type="checkbox" checked={form.scopes.includes(s)}
                            onChange={() => toggleScope(s)}
                            data-testid={`pp-key-scope-${s}`}
                            style={{ marginRight: "0.25rem" }} />
                    <code style={{ color: "#5EEAD4" }}>{s}</code>
                  </label>
                ))}
                {allowedScopes.length === 0 && (
                  <span style={{ color: "#F59E0B", fontSize: "0.8rem" }}>
                    Your organization has no scopes yet. Contact HarborWell to configure.
                  </span>
                )}
              </div>
            </label>
            <label className="hw-pp__field">
              <span className="hw-pp__field-label">
                Custom rate limit / min (optional — leave blank for the default)
              </span>
              <input className="hw-pp__input" type="number" min={1} max={6000}
                      value={form.rate_limit_per_min || ""}
                      onChange={(e) => setForm({...form, rate_limit_per_min: e.target.value ? +e.target.value : null})}
                      data-testid="pp-key-rate-limit" />
            </label>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <button className="hw-pp__btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="hw-pp__btn hw-pp__btn--primary" onClick={create}
                       disabled={busy || form.name.trim().length < 2 || allowedScopes.length === 0}
                       data-testid="pp-key-submit">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Mint key
              </button>
            </div>
          </div>
        )}

        {loading && !keys.length ? (
          <div style={{ color: "#5EEAD4" }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
          </div>
        ) : keys.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>
            No API keys yet. {canWrite ? "Click \"New key\" to mint one." : "Ask your Owner/Admin to mint one."}
          </div>
        ) : (
          <table className="hw-pp__table" data-testid="pp-keys-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Requests</th>
                <th>Status</th>
                <th>Last used</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} data-testid={`pp-key-row-${k.id}`}>
                  <td><strong style={{ color: "#F1F5F9" }}>{k.name}</strong></td>
                  <td className="hw-pp__mono">{k.prefix}...</td>
                  <td>
                    {(k.scopes || []).slice(0, 4).map((s) => (
                      <span key={s} className="hw-pp__scope-chip">{s}</span>
                    ))}
                    {(k.scopes || []).length > 4 && (
                      <span style={{ fontSize: "0.7rem", color: "#64748B" }}>
                        +{k.scopes.length - 4} more
                      </span>
                    )}
                  </td>
                  <td>{new Intl.NumberFormat().format(k.request_count || 0)}</td>
                  <td>
                    <span className={`hw-pp__pill ${k.revoked ? "hw-pp__pill--revoked" : "hw-pp__pill--active"}`}>
                      {k.revoked ? "Revoked" : "Active"}
                    </span>
                  </td>
                  <td style={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                    {k.last_used_at ? k.last_used_at.slice(0, 10) : "—"}
                  </td>
                  <td>
                    {!k.revoked && canWrite && (
                      <button className="hw-pp__btn hw-pp__btn--danger"
                               onClick={() => revoke(k.id)}
                               data-testid={`pp-key-revoke-${k.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
